// Persistent Video Player Component - Ne se réinitialise jamais
// Supports Picture-in-Picture, Timer overlay, and state persistence
import { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Play, Pause, X, Minimize2, Maximize2, Timer, Clock, Flame, 
  Star, CalendarPlus, Share2, Check, RotateCcw,
  PictureInPicture2
} from 'lucide-react';

const DIFFICULTY_LABELS = {
  beginner: { label: 'Débutant', color: 'bg-green-500' },
  intermediate: { label: 'Intermédiaire', color: 'bg-yellow-500' },
  advanced: { label: 'Avancé', color: 'bg-red-500' },
  expert: { label: 'Expert', color: 'bg-red-500' },
};

// Calculate initial timer seconds from video
const getInitialTimerSeconds = (video) => {
  if (video?.duration_minutes) {
    return video.duration_minutes * 60;
  } else if (video?.duration) {
    const parts = video.duration.split(':');
    const mins = parseInt(parts[0]) || 30;
    return mins * 60;
  }
  return 30 * 60;
};

export default function PersistentVideoPlayer({
  video,
  onClose,
  onComplete,
  onFavorite,
  onAddToAgenda,
  onShare,
  isFavorite = false,
  isMinimized = false,
  onMinimizeChange,
}) {
  // Calculate initial timer value
  const initialTimerSeconds = useMemo(() => getInitialTimerSeconds(video), [video]);
  
  const [isPiPActive, setIsPiPActive] = useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(initialTimerSeconds);
  const [timerRunning, setTimerRunning] = useState(false);
  const [minimized, setMinimized] = useState(isMinimized);
  const [currentTime, setCurrentTime] = useState(0);
  
  const iframeRef = useRef(null);
  const timerIntervalRef = useRef(null);

  // Reset timer when video changes
  useEffect(() => {
    setTimerSeconds(initialTimerSeconds);
  }, [initialTimerSeconds]);

  // Timer logic - completely independent from video
  useEffect(() => {
    if (timerRunning && timerSeconds > 0) {
      timerIntervalRef.current = setInterval(() => {
        setTimerSeconds(prev => {
          if (prev <= 1) {
            setTimerRunning(false);
            onComplete?.();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerIntervalRef.current);
  }, [timerRunning, onComplete]);

  // Format time display
  const formatTime = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle minimize toggle - video continues playing
  const handleMinimize = () => {
    const newState = !minimized;
    setMinimized(newState);
    onMinimizeChange?.(newState);
  };

  // Handle close - but save position first
  const handleClose = () => {
    // Save current position in localStorage for this video
    if (video?.id && currentTime > 0) {
      const savedPositions = JSON.parse(localStorage.getItem('videoPositions') || '{}');
      savedPositions[video.id] = currentTime;
      localStorage.setItem('videoPositions', JSON.stringify(savedPositions));
    }
    onClose?.();
  };

  // Try to enable Picture-in-Picture
  const togglePiP = async () => {
    try {
      // For YouTube embeds, PiP is not directly controllable
      // But we can minimize the player to simulate it
      if (isPiPActive) {
        setIsPiPActive(false);
        setMinimized(false);
      } else {
        setIsPiPActive(true);
        setMinimized(true);
      }
    } catch (err) {
      console.log('PiP not available');
    }
  };

  // Reset timer
  const resetTimer = () => {
    setTimerRunning(false);
    const mins = video?.duration_minutes || 30;
    setTimerSeconds(mins * 60);
  };

  // Progress percentage for timer
  const initialSeconds = (video?.duration_minutes || 30) * 60;
  const timerProgress = ((initialSeconds - timerSeconds) / initialSeconds) * 100;

  if (!video) return null;

  // Minimized PiP-style view - floats at bottom right
  if (minimized || isPiPActive) {
    return (
      <div 
        className="fixed bottom-20 right-4 z-50 w-72 bg-background border rounded-xl shadow-2xl overflow-hidden"
        data-testid="video-player-minimized"
      >
        {/* Mini video preview */}
        <div className="relative aspect-video bg-black">
          {video.youtube_id ? (
            <iframe
              ref={iframeRef}
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${video.youtube_id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
              title={video.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div 
              className="absolute inset-0 flex items-center justify-center"
              style={{ background: video.category_color || '#6366f1' }}
            >
              <span className="text-4xl">{video.category_icon || '🏋️'}</span>
            </div>
          )}
          
          {/* Controls overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
            <div className="flex items-center justify-between">
              <span className="text-white text-xs truncate flex-1 mr-2">{video.title}</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => { setMinimized(false); setIsPiPActive(false); }}
                  className="p-1 rounded bg-white/20 hover:bg-white/30"
                >
                  <Maximize2 className="w-3 h-3 text-white" />
                </button>
                <button
                  onClick={handleClose}
                  className="p-1 rounded bg-white/20 hover:bg-white/30"
                >
                  <X className="w-3 h-3 text-white" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Timer bar if active */}
        {showTimer && (
          <div className="p-2 bg-muted/50 flex items-center gap-2">
            <div className="relative w-8 h-8">
              <svg className="w-8 h-8 transform -rotate-90">
                <circle cx="16" cy="16" r="12" fill="none" stroke="currentColor" strokeWidth="3" className="text-muted" />
                <circle cx="16" cy="16" r="12" fill="none" stroke="currentColor" strokeWidth="3"
                  strokeDasharray={`${timerProgress * 0.75} 100`} className="text-primary" />
              </svg>
            </div>
            <span className="font-mono text-sm font-bold">{formatTime(timerSeconds)}</span>
            <button
              onClick={() => setTimerRunning(!timerRunning)}
              className="ml-auto p-1 rounded-full bg-primary text-white"
            >
              {timerRunning ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Full player view - Modal style but persistent
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="video-player-full">
      <div className="bg-background rounded-xl overflow-hidden w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Video area */}
        <div className="relative aspect-video bg-black flex-shrink-0">
          {video.youtube_id ? (
            <iframe
              ref={iframeRef}
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${video.youtube_id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
              title={video.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div 
              className="absolute inset-0 flex flex-col items-center justify-center text-white p-4"
              style={{ background: `linear-gradient(135deg, ${video.category_color || '#6366f1'} 0%, ${video.category_color || '#6366f1'}99 100%)` }}
            >
              <span className="text-5xl mb-3">{video.category_icon || '🏋️'}</span>
              <h3 className="text-xl font-bold text-center">{video.title}</h3>
            </div>
          )}

          {/* Timer overlay on video (if active and running) */}
          {showTimer && (
            <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-sm rounded-xl p-3">
              <div className="flex items-center gap-3">
                <div className="relative w-12 h-12">
                  <svg className="w-12 h-12 transform -rotate-90">
                    <circle cx="24" cy="24" r="20" fill="none" stroke="currentColor" strokeWidth="4" className="text-white/30" />
                    <circle cx="24" cy="24" r="20" fill="none" stroke="currentColor" strokeWidth="4"
                      strokeDasharray={`${timerProgress * 1.26} 1000`} strokeLinecap="round" className="text-primary" />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-mono font-bold">
                    {formatTime(timerSeconds)}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => setTimerRunning(!timerRunning)}
                    className={`p-2 rounded-full ${timerRunning ? 'bg-orange-500' : 'bg-primary'} text-white`}
                  >
                    {timerRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                  <button onClick={resetTimer} className="p-2 rounded-full bg-white/20 text-white">
                    <RotateCcw className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Top controls */}
          <div className="absolute top-2 left-2 right-2 flex justify-between">
            <button
              onClick={togglePiP}
              className="p-2 rounded-full bg-black/50 hover:bg-black/70 text-white"
              title="Picture-in-Picture"
            >
              <PictureInPicture2 className="w-4 h-4" />
            </button>
            <div className="flex gap-2">
              <button
                onClick={handleMinimize}
                className="p-2 rounded-full bg-black/50 hover:bg-black/70 text-white"
              >
                <Minimize2 className="w-4 h-4" />
              </button>
              <button
                onClick={handleClose}
                className="p-2 rounded-full bg-black/50 hover:bg-black/70 text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Info & Controls panel */}
        <div className="p-4 space-y-3 overflow-y-auto">
          {/* Title & metadata */}
          <div>
            <h3 className="font-semibold text-lg">{video.title}</h3>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <Badge className={DIFFICULTY_LABELS[video.level || video.difficulty]?.color}>
                {DIFFICULTY_LABELS[video.level || video.difficulty]?.label || 'Intermédiaire'}
              </Badge>
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {video.duration || '30:00'}
              </span>
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Flame className="w-3 h-3" />
                ~{video.calories_estimate || (video.duration_minutes || 30) * 10} kcal
              </span>
            </div>
          </div>

          {/* Timer control button */}
          <Button
            variant={showTimer ? "default" : "outline"}
            className={`w-full ${showTimer ? 'bg-primary' : 'bg-primary/10 hover:bg-primary/20 border-primary/30'}`}
            onClick={() => {
              setShowTimer(true);
              setTimerRunning(true);
            }}
          >
            <Timer className="w-4 h-4 mr-2" />
            {showTimer 
              ? `Timer actif: ${formatTime(timerSeconds)}` 
              : `Démarrer le timer (${video.duration || '30:00'})`
            }
          </Button>

          {/* Equipment info */}
          <p className="text-sm">
            <span className="font-medium">Équipement:</span>{' '}
            <span className="text-muted-foreground">{video.equipment || 'Aucun'}</span>
          </p>

          {/* Quick actions */}
          <div className="flex gap-2 py-2 border-y">
            <Button 
              variant={isFavorite ? "default" : "ghost"} 
              size="sm"
              onClick={() => onFavorite?.(video)}
              className="flex-1"
            >
              <Star className={`w-4 h-4 mr-1 ${isFavorite ? 'fill-current' : ''}`} />
              Favoris
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => onAddToAgenda?.(video)}
              className="flex-1"
            >
              <CalendarPlus className="w-4 h-4 mr-1" />
              Agenda
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => onShare?.(video)}
              className="flex-1"
            >
              <Share2 className="w-4 h-4 mr-1" />
              Partager
            </Button>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 pt-2">
            <Button 
              className="flex-1" 
              size="lg"
              onClick={() => {
                setTimerRunning(false);
                onComplete?.();
              }}
            >
              <Check className="w-5 h-5 mr-2" />
              Terminé !
            </Button>
            <Button variant="outline" size="lg" onClick={handleMinimize}>
              Minimiser
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
